import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { githubLight, githubDark } from '@uiw/codemirror-theme-github';
import { Compartment, type EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { codeFolding, foldKeymap, foldService } from '@codemirror/language';
import type { FoldRange } from '@slate/core';
import { markdownShortcuts } from '../keymap/markdownKeymap';

// `languages` from @codemirror/language-data is a `LanguageDescription[]`. We
// derive the types from it (rather than importing @codemirror/language, which is
// only a transitive dep and not resolvable here) so there are NO new deps.
type LanguageDescription = (typeof languages)[number];
/** The `LanguageSupport` produced by `LanguageDescription.load()`. */
type LanguageSupport = Awaited<ReturnType<LanguageDescription['load']>>;

export interface PlainTextEditorProps {
  value: string;
  dark: boolean;
  onChange: (value: string) => void;
  /** Reports CodeMirror's scroll element (for split-view scroll sync). */
  onScroller?: (el: HTMLElement | null) => void;
  /**
   * Reports the live CodeMirror `EditorView` (for content-aware scroll sync and
   * outline click-to-jump, which need source-line ↔ position mapping). `null`
   * on unmount.
   */
  onView?: (view: EditorView | null) => void;
  /**
   * Active document's file name, used to match a CodeMirror language for syntax
   * highlighting of code files. Ignored when `markdown` is true.
   */
  fileName?: string;
  /**
   * When true (default) the buffer is edited as Markdown (markdown language +
   * shortcuts). When false it's a code/plain-text file and the language is
   * matched from `fileName` and lazily loaded.
   */
  markdown?: boolean;
  /** Soft-wrap long lines via CodeMirror's `EditorView.lineWrapping`. Default false. */
  softWrap?: boolean;
  /** Invoked by the in-editor Alt+Z binding to toggle wrap for the active document. */
  onToggleWrap?: () => void;
  /**
   * LINE-based fold ranges (1-based inclusive) supplied by the app's fold
   * providers. Drive the fold gutter via a reconfigurable `foldService`
   * compartment. Stale ranges (computed for a longer document) are clamped, never
   * thrown, so folding is safe while typing. Default none.
   */
  foldRanges?: readonly FoldRange[];
  /**
   * Whether code folding is available at all. Folding is owned by a fold-provider
   * plugin (e.g. Slate Coder); when no provider is active for the current file the
   * app passes `false` and folding is fully OFF: no fold gutter, no fold keymap,
   * no fold state. Default `true` (folding installed). Switching this rebuilds the
   * extension set, so the fold compartment exists only while enabled.
   */
  foldingEnabled?: boolean;
}

/** The shape of a CodeMirror `foldService` function. */
type FoldServiceFn = (
  state: EditorState,
  lineStart: number,
  lineEnd: number,
) => { from: number; to: number } | null;

/**
 * Build a `foldService` from line-based ranges. The lookup map (start line ->
 * widest end line) is built ONCE per change for O(1) probing; the service maps
 * lines to offsets against the LIVE state on each call (so it stays correct as
 * the document edits) and clamps any range that now exceeds the document.
 */
function serviceFromRanges(ranges: readonly FoldRange[]): FoldServiceFn {
  const byStart = new Map<number, number>();
  for (const r of ranges) {
    if (r.endLine > r.startLine) {
      const cur = byStart.get(r.startLine);
      if (cur === undefined || r.endLine > cur) byStart.set(r.startLine, r.endLine);
    }
  }
  return (state, lineStart, lineEnd) => {
    if (byStart.size === 0) return null;
    const lines = state.doc.lines;
    const startNo = state.doc.lineAt(lineStart).number;
    const endNo = byStart.get(startNo);
    if (endNo === undefined) return null;
    // Fold from the END of the start line (it stays visible) to the END of the
    // end line. Clamp a stale end past the current document length.
    const clampedEnd = Math.min(endNo, lines);
    if (clampedEnd <= startNo) return null;
    const to = state.doc.line(clampedEnd).to;
    return to > lineEnd ? { from: lineEnd, to } : null;
  };
}

/**
 * Match a `LanguageDescription` to a file name by extension/alias — the same
 * behaviour as CodeMirror's `LanguageDescription.matchFilename`, reimplemented
 * here so we don't depend on (the non-hoisted) `@codemirror/language`.
 */
function matchLanguage(fileName: string): LanguageDescription | null {
  for (const desc of languages) {
    for (const ext of desc.extensions) {
      if (fileName.endsWith(`.${ext}`)) return desc;
    }
  }
  return null;
}

/** Raw markdown / code editing via CodeMirror 6. */
export function PlainTextEditor({
  value,
  dark,
  onChange,
  onScroller,
  onView,
  fileName,
  markdown: isMarkdown = true,
  softWrap = false,
  onToggleWrap,
  foldRanges,
  foldingEnabled = true,
}: PlainTextEditorProps): ReactElement {
  // For code files, the language support is loaded asynchronously (CodeMirror
  // languages are code-split). `null` = no match (or not yet loaded) → plain.
  const [codeLang, setCodeLang] = useState<LanguageSupport | null>(null);

  // A stable compartment so fold ranges can be reconfigured live (without
  // rebuilding the whole extension set, which would churn editor state).
  const foldCompartmentRef = useRef<Compartment | null>(null);
  if (!foldCompartmentRef.current) foldCompartmentRef.current = new Compartment();
  const foldCompartment = foldCompartmentRef.current;
  // Local handle to the live view so the fold effect can dispatch reconfigures.
  const viewRef = useRef<EditorView | null>(null);

  // Resolve + lazily load a language for code files. Guarded against races (the
  // file/markdown flag may change mid-load) and unmount.
  useEffect(() => {
    if (isMarkdown) {
      setCodeLang(null);
      return;
    }
    let cancelled = false;
    setCodeLang(null);
    const desc = fileName ? matchLanguage(fileName) : null;
    if (!desc) return; // unknown extension (e.g. .txt) → render plain
    void desc
      .load()
      .then((support) => {
        if (!cancelled) setCodeLang(support);
      })
      .catch(() => {
        /* language failed to load → stay plain */
      });
    return () => {
      cancelled = true;
    };
  }, [isMarkdown, fileName]);

  const extensions = useMemo<Extension[]>(() => {
    const base: Extension[] = isMarkdown
      ? [markdownShortcuts, markdown({ base: markdownLanguage, codeLanguages: languages })]
      : codeLang
        ? [codeLang]
        : [];
    if (softWrap) base.push(EditorView.lineWrapping);
    // Alt+Z toggles wrap from inside the editor. The global key handler in
    // App.tsx deliberately ignores Alt, so binding it here (where focus is) is
    // the right home and matches VS Code.
    base.push(
      keymap.of([
        {
          key: 'Alt-z',
          run: () => {
            onToggleWrap?.();
            return true;
          },
        },
      ]),
    );
    // Folding: the fold state field + fold keybindings, plus a compartment for the
    // (dynamic) fold service. The compartment starts empty and is filled by the
    // effect below, so this memo need not depend on `foldRanges`. Installed only
    // when a fold provider is active for this file; otherwise folding is fully off.
    if (foldingEnabled) {
      base.push(codeFolding(), keymap.of(foldKeymap), foldCompartment.of([]));
    }
    return base;
  }, [isMarkdown, codeLang, softWrap, onToggleWrap, foldCompartment, foldingEnabled]);

  // Push the current fold ranges into the compartment whenever they change (or
  // on first mount once the view exists). Reconfiguring a compartment is cheap
  // and leaves the rest of the editor state untouched.
  useEffect(() => {
    // When folding is disabled the compartment is not part of the extension set,
    // so reconfiguring it would throw — skip entirely.
    if (!foldingEnabled) return;
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: foldCompartment.reconfigure(foldService.of(serviceFromRanges(foldRanges ?? []))),
    });
  }, [foldRanges, foldCompartment, foldingEnabled]);

  useEffect(
    () => () => {
      onScroller?.(null);
      onView?.(null);
    },
    [onScroller, onView],
  );

  return (
    <div className="mp-editor" data-testid="plaintext-editor">
      <CodeMirror
        value={value}
        onChange={onChange}
        onCreateEditor={(view) => {
          viewRef.current = view;
          onScroller?.(view.scrollDOM);
          onView?.(view);
          // Seed the fold service immediately (the [foldRanges] effect may have
          // already run before the view existed). Skipped when folding is
          // disabled, since the compartment is not in the extension set.
          if (foldingEnabled) {
            view.dispatch({
              effects: foldCompartment.reconfigure(
                foldService.of(serviceFromRanges(foldRanges ?? [])),
              ),
            });
          }
        }}
        theme={dark ? githubDark : githubLight}
        extensions={extensions}
        height="100%"
        style={{ height: '100%' }}
        basicSetup={{
          lineNumbers: true,
          foldGutter: foldingEnabled,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          autocompletion: false,
        }}
      />
    </div>
  );
}
